import { apiUrl, buildRequestInit } from './api';

// Client-side activity capture (admin metrics) — the events only the SPA can
// see: sign-in and page views with dwell. Actions are logged server-side. Every
// send is fire-and-forget: it never throws, never redirects, never blocks the
// UI — metrics must not affect what the user is doing. `keepalive` lets a
// page-view flush survive the tab closing. (sendBeacon can't set the CSRF
// header, so a keepalive fetch is used instead.)

interface ClientEvent {
  kind: 'login' | 'page_view';
  label?: string;
  durationMs?: number;
}

function send(events: ClientEvent[]): void {
  if (events.length === 0) return;
  try {
    // The CSRF + JSON-body contract lives in buildRequestInit (one home for it);
    // we only add `keepalive` so the flush survives the tab closing. Still a
    // bare fetch — NOT the api() client — because metrics must never 401-redirect
    // or unwrap an envelope (sanctioned in api-consolidation.test.ts).
    void fetch(apiUrl('/activity'), {
      ...buildRequestInit('POST', { events }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never throw from metrics
  }
}

// Sign-in. Guarded to once per browser-tab session so a refresh (which re-runs
// the /me probe) doesn't inflate the login count — only a genuine new session
// records a login. Approximate by design.
// `landedOn` is the page this session opened on. It rides ON the login event
// because it cannot be recovered later: a page view is only written when the
// view CHANGES, so someone who signs in and stays put produces no page view at
// all, and the admin feed had nothing to name (Freddi 2026-07-26). The server
// holds it to the same page-label allowlist as everything else it renders, and
// omitting it stays valid.
/**
 * WHAT A LOGIN SAYS IT LANDED ON, or nothing (PR #228 review, codex).
 *
 * The server holds every recorded label to the allowlist for the role that sent
 * it, and a CUSTOMER reaches no labelled page the desk's feed knows about — so
 * it rejects any label from one with a 400, correctly. The client was sending
 * `pathToView(pathname)`, which falls through to the admin default `today` on a
 * customer URL, so every customer sign-in was refused WHOLE: gone from the
 * activity feed, from per-user login counts and from the sign-ins chart. A
 * label the server will not take should not cost the event that carries it.
 *
 * The endpoint already accepts a login with no label, and that is what a
 * customer sends. Pure and exported so the choice is a unit test rather than a
 * ternary buried in a mount effect.
 */
export function loginLabelFor(
  role: string | null | undefined,
  pathname: string,
  pathToView: (p: string) => string = (p) => p,
  staffToday = 'today',
): string | undefined {
  // A NULL ROLE IS NOT A CUSTOMER and is not staff — it falls through to the
  // path, which is what the code did before this was extracted. The type says
  // nullable because `me.role` is, rather than the call site casting the
  // problem away.
  if (role === 'customer') return undefined;
  if (role === 'staff') return staffToday;
  return pathToView(pathname);
}

export function recordLogin(landedOn?: string, who?: string): void {
  // ONCE PER PERSON PER TAB (PR #267 review): the newest sign-in decides who a message goes to, so a
  // second person signing in in the same tab is recorded — the guard holds the identity, not a flag.
  const mark = who ? `1:${who.toLowerCase()}` : '1';
  try {
    if (sessionStorage.getItem('bbbk_login_logged') === mark) return;
    sessionStorage.setItem('bbbk_login_logged', mark);
  } catch {
    // sessionStorage unavailable — fall through and log (worst case: a dup).
  }
  send([landedOn ? { kind: 'login', label: landedOn } : { kind: 'login' }]);
}

// A page view that has ended, with how long it was shown (active time between
// navigations — approximate; idle/backgrounded time isn't subtracted).
export function recordPageView(label: string, durationMs: number): void {
  if (!label) return;
  send([{ kind: 'page_view', label, durationMs: Math.max(0, Math.round(durationMs)) }]);
}
